import { Switch } from '../../components/ui/switch';
import SubjectTabs from '../generic/SubjectTabs';
import { Info, PercentIcon } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import StatCell from '../generic/StatCell';
import { QSTATUS } from '../../../../../apps/frontend/src/utils/strings';
import TableLoader from '../generic/TableLoader';
import moment from 'moment';
import { CheckSquareIcon, XSquareIcon, WarningDiamondIcon } from '@phosphor-icons/react';
import TableHeader from '../../components/generic/TableHeader';
import NoData from '../generic/NoData';
import { useNavigate } from 'react-router-dom';

const DifficultyTestBreakdown = ({ data, loading, error, packId, onResetFilters }) => {
  const [activeSubject, setActiveSubject] = useState('overall');
  const [showPercentage, setShowPercentage] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const navigate = useNavigate();

  const onTogglePercentage = () => {
    setShowPercentage(!showPercentage);
  };

  // Extract and set subjects from data
  useEffect(() => {
    if (data && data.length > 0) {
      const uniqueSubjects = [];
      const seenSubjects = new Set();

      data.forEach((test) => {
        test.detailedAnalysis?.subjectWise?.forEach((subject) => {
          if (subject.subjectMeta && !seenSubjects.has(subject.subjectMeta._id)) {
            seenSubjects.add(subject.subjectMeta._id);
            uniqueSubjects.push({
              _id: subject.subjectMeta._id,
              title: subject.subjectMeta.title,
              icon: subject.subjectMeta.icon,
              color: subject.subjectMeta.color,
              gradient: subject.subjectMeta.gradient,
              gradientDark: subject.subjectMeta.gradientDark,
            });
          }
        });
      });

      setSubjects(uniqueSubjects);

      // Set initial subject if not already set
      if (uniqueSubjects.length > 0 && !activeSubject) {
        setActiveSubject(uniqueSubjects[0]._id);
      }
    }
  }, [data, activeSubject]);

  const getCurrentSubjectData = (testData) => {
    return testData?.detailedAnalysis?.subjectWise?.find(
      (subject) => subject.subjectMeta._id === activeSubject
    );
  };

  const getPercentage = (num, total) => (total > 0 ? Math.round((num / total) * 100) : 0);

  return (
    <div>
      <TableHeader
        headerTitle="Test-wise Breakdown"
        headerSwitch={
          <span className="flex items-center gap-1">
            <PercentIcon size={16} weight="bold" />
            Show Percentage
          </span>
        }
        headerSwitchCase={showPercentage}
        headerSwitchHandler={onTogglePercentage}
        showBottomBorder={false}
      />

      {/* ✅ Legend (above SubjectTabs) */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 text-sm border-r border-l border-[var(--q3-stroke-light)] ">
        <div className="flex items-center gap-2">
          <CheckSquareIcon size={16} className="text-[var(--q3-green-normal)]" />
          <span className="text-[var(--q3-green-normal)]">Correct Attempts</span>
        </div>
        <div className="flex items-center gap-2">
          <XSquareIcon size={16} className="text-[var(--q3-danger-normal)]" />
          <span className="text-[var(--q3-danger-normal)]">Incorrect Attempts</span>
        </div>
        <div className="flex items-center gap-2">
          <WarningDiamondIcon size={16} className="text-[var(--q3-neutral-light)]" />
          <span className="text-[var(--q3-neutral-light)]">Not Attempted</span>
        </div>
      </div>

      {/* ✅ SubjectTabs in its own horizontal scroll container */}
      <div className="overflow-x-auto border-r border-l border-[var(--q3-stroke-light)] bg-white mb-[-1px]">
        <div className="min-w-0">
          <SubjectTabs activeSubject={activeSubject} onSubjectChange={setActiveSubject} subjects={subjects} />
        </div>
      </div>

      {/* ✅ Main scrollable content container */}
      <div className="overflow-x-auto">
        <div className="min-w-[700px] border-r border-l border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
          <TableLoader loading={loading}>
            {error ? (
              <div className="text-red-500 p-4">Error loading data: {error.message}</div>
            ) : !data || data.length === 0 ? (
              <NoData onResetAll={onResetFilters} />
            ) : (
              <>
                {/* Table Header */}
                <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
                  <div className="grid grid-cols-4 divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]">
                    <div className="p-4 text-[var(--q3-neutral-default)]">Test Title</div>
                    <div className="p-4 text-center text-[var(--q3-neutral-default)]">Easy</div>
                    <div className="p-4 text-center text-[var(--q3-neutral-default)]">Moderate</div>
                    <div className="p-4 text-center text-[var(--q3-neutral-default)]">Tough</div>
                  </div>
                </div>

                {/* Table Rows */}
                {data.map((test, index) => {
                  const subjectData = getCurrentSubjectData(test);
                  if (!subjectData) return null;

                  const difficultyBlocks = (levelData) => {
                    const total = levelData.correct + levelData.incorrect + levelData.unanswered;
                    return (
                      <div className="flex items-center justify-center gap-2 text-sm p-4">
                        <div className="flex items-center gap-1 text-[var(--q3-neutral-default)]">
                          <CheckSquareIcon size={14} className="text-green-500" />
                          <span>
                            {showPercentage ? `${getPercentage(levelData.correct, total)}%` : levelData.correct}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[var(--q3-neutral-default)]">
                          <XSquareIcon size={14} className="text-red-500" />
                          <span>
                            {showPercentage ? `${getPercentage(levelData.incorrect, total)}%` : levelData.incorrect}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[var(--q3-neutral-default)]">
                          <WarningDiamondIcon size={14} className="text-gray-400" />
                          <span>
                            {showPercentage ? `${getPercentage(levelData.unanswered, total)}%` : levelData.unanswered}
                          </span>
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div
                      key={index}
                      className="grid grid-cols-4 divide-x divide-[var(--q3-stroke-light)] border-b border-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]"
                    >
                      {/* ✅ Test Title (clickable) */}
                      <div
                        className="p-4 cursor-pointer hover:bg-[var(--q3-surface-hover)]"
                        onClick={() =>
                          navigate(`/${test.packId}/${test.testId}/analysis`)
                        }
                      >
                        <div className="font-medium truncate whitespace-nowrap overflow-hidden max-w-[200px]">
                          {test.detailedAnalysis.testTitle}
                        </div>
                        <div className="text-sm text-[var(--q3-neutral-light)]">
                          {moment(test.detailedAnalysis.submittedAt).format('D MMM YYYY')}
                          {test.detailedAnalysis.isBonusAvailable &&
                            test.detailedAnalysis.isBonusAttempted &&
                            ' | Bonus Qs Attempted'}
                        </div>
                      </div>

                      {/* Easy */}
                      {difficultyBlocks(subjectData.easy)}

                      {/* Moderate */}
                      {difficultyBlocks(subjectData.moderate)}

                      {/* Tough */}
                      {difficultyBlocks(subjectData.tough)}
                    </div>
                  );
                })}
              </>
            )}
          </TableLoader>
        </div>
      </div>
    </div>
  );
};

export default DifficultyTestBreakdown;
